import { useQuery } from '@tanstack/react-query';
import { useApiClient } from '../../../helpers/apiClient.helper';
import { worldCupQueryKeys } from '../constants/worldcupQueryKeys';
import {
  GC_TIME_WORLD_CUP_MS,
  STALE_TIME_WORLD_CUP_LEADERBOARD_MS,
} from '../constants/worldcupReactQuery.constants';
import type { WorldCupGroupCompanyBranding } from '../types/worldcup.types';

export interface GroupsApiMemberPreview {
  displayName: string;
  avatarUrl: string | null;
  avatarId: string | null;
}

export interface GroupsApiEntry {
  id: string;
  name: string;
  inviteCode?: string | null;
  description?: string | null;
  memberCount: number;
  userPoints: number;
  userRank: number;
  leadingUserName: string | null;
  memberPreview?: GroupsApiMemberPreview[];
  customLogoUrl: string | null;
  squadLocked: boolean;
  userRole: string;
  entryFeeCoins?: number;
  payoutMode?: string;
  payoutFirstPct?: number | null;
  payoutSecondPct?: number | null;
  payoutThirdPct?: number | null;
  crestColor?: string | null;
  crestEmblem?: string | null;
  companyBranding: WorldCupGroupCompanyBranding | null;
}

interface GroupsApiPayload {
  success: boolean;
  error?: string;
  data: GroupsApiEntry[];
  count: number;
}

export function worldCupGroupsQueryOptions(
  apiClient: ReturnType<typeof useApiClient>,
  competitionId: string,
) {
  return {
    queryKey: worldCupQueryKeys.groups(competitionId),
    queryFn: async () => {
      const response = await apiClient<GroupsApiPayload>(
        `/competitions/${competitionId}/groups`,
      );
      const payload = response.data;
      if (!payload.success) {
        throw new Error(payload.error || 'Failed to fetch WC groups');
      }
      return payload.data;
    },
    staleTime: STALE_TIME_WORLD_CUP_LEADERBOARD_MS,
    gcTime: GC_TIME_WORLD_CUP_MS,
    refetchOnWindowFocus: false,
  };
}

export function useWorldCupGroupsQuery(competitionId: string, enabled: boolean = true) {
  const apiClient = useApiClient();

  return useQuery({
    ...worldCupGroupsQueryOptions(apiClient, competitionId),
    enabled,
  });
}
